import React, { Fragment, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { searchClient } from '../components/Search/core';
import UserCreativeCardModern from '../components/User/UserCreativeCardModern';
import { MessageContextProvider } from '../contexts/messageContext';
import ContentLoader from '../components/ContentLoader';
import Spinner from '../components/Spinner';
import InfiniteScroll from 'react-infinite-scroll-component';
import type { IClientUser } from '../interfaces/user';
import { UserData } from '../types/user';

const PAGE_LIMIT = 12;

const SearchResultsPage: React.FC<IClientUser> = props => {
    const [users, setUsers] = useState<UserData[]>([]);
    const [totalResults, setTotalResults] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');

    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const query = urlParams.get('q') ?? '';

    useEffect(() => {
        if (query) {
            setSearchQuery(query);
            handleSearch(query, 0, true);
        }
    }, [query]);

    const handleSearch = async (searchTerm: string, page = 0, reset = false) => {
        try {
            setIsLoading(true);
            const response = await searchClient.search([
                {
                    indexName: 'user',
                    params: {
                        query: searchTerm,
                        hitsPerPage: PAGE_LIMIT,
                        page,
                        attributesToRetrieve: ['*']
                    }
                }
            ]);

            const hits = response.results[0].hits;
            const count = response.results[0].nbHits;

            if (reset) {
                setUsers(hits);
                setCurrentPage(0);
            } else {
                setUsers(prev => [...prev, ...hits]);
            }

            setTotalResults(count);
            setCurrentPage(page);
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadMore = async () => {
        const nextPage = currentPage + 1;
        await handleSearch(searchQuery, nextPage, false);
    };

    const CreativesCardLoader = (
        <Fragment>
            <div className="column">
                <ContentLoader />
            </div>
            <div className="column">
                <ContentLoader />
            </div>
            <div className="column">
                <ContentLoader />
            </div>
        </Fragment>
    );

    if (isLoading && users.length === 0) {
        return (
            <main className="page-search-results">
                <Navigation {...props} />
                <section className="container">
                    <div className="columns section">
                        <div className="column">
                            <h1 className="is-size-3 has-text-grey-dark has-text-weight-semibold">
                                Searching...
                            </h1>
                        </div>
                    </div>
                    <div className="columns">{CreativesCardLoader}</div>
                </section>
            </main>
        );
    }

    return (
        <main className="page-search-results">
            <Fragment>
                <Navigation {...props} />
                <section className="container">
                    <div className="columns section is-vcentered p-b-0">
                        <div className="column">
                            <h1 className="is-size-3 has-text-grey-dark has-text-weight-semibold">
                                Search Results
                            </h1>
                            {searchQuery && (
                                <p className="is-size-5 has-text-black">
                                    {totalResults} results for "{searchQuery}"
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="columns p-r-1 p-l-1">
                        <div className="column">
                            <InfiniteScroll
                                className="columns page-creatives-userlist is-multiline"
                                dataLength={users.length}
                                hasMore={users.length < totalResults}
                                loader={
                                    <div className="column is-12 page-creatives-userlist-loader">
                                        <Spinner />
                                    </div>
                                }
                                next={loadMore}
                                scrollThreshold={0.8}
                                endMessage={
                                    <p className="column has-text-centered m-b-3 is-12">
                                        {users.length === 0
                                            ? `No results found for "${searchQuery}"`
                                            : 'No more results to show.'}
                                    </p>
                                }
                            >
                                {users &&
                                    users.map((user: UserData, idx: number) => (
                                        <MessageContextProvider key={idx} currentUser={props.user!}>
                                            <UserCreativeCardModern user={user} currentUser={props.user!} />
                                        </MessageContextProvider>
                                    ))}
                            </InfiniteScroll>
                        </div>
                    </div>
                </section>
            </Fragment>
        </main>
    );
};

export default SearchResultsPage;
